<script setup>
// Auto-extracted chrome for MySolar24.vue — header + footer copied verbatim
// from Home so legal pages (Impressum/Datenschutz/AGB) render with the
// pilot's own masthead + footer (no AppLayout chrome leak).
// Body markup of the wrapper is yielded via <slot />.
import { computed } from 'vue';
import { Head } from '@inertiajs/vue3';

const props = defineProps({
    domain: { type: Object, default: () => ({}) },
    brandName: { type: String, default: "My Solar 24" },
    brandTagline: { type: String, default: "Ihr Solarprojekt - transparent verfolgt" },
    brandAccent: { type: String, default: '#0EA5E9' },
    author: { type: Object, default: null },
    designVersion: { type: String, default: '' },
});

// Derive a few values that the chrome HTML references. Best-effort fallbacks
// for the values the chrome's masthead/footer reads.
const today = computed(() => new Date().toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' }));
const brandName = computed(() => props.brandName || props.domain?.settings?.brand_name || "My Solar 24");
const brandTagline = computed(() => props.brandTagline || props.domain?.settings?.brand_tagline || "Ihr Solarprojekt - transparent verfolgt");
const brandAccent = computed(() => props.brandAccent);
const author = computed(() => props.author);
const DESIGN_VERSION = computed(() => props.designVersion || '');


const fontsHref = "https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@500;600&display=swap";
</script>

<template>
    <div class="ms2-root" :style="`--ms2-accent:${brandAccent};`">
        <Head>
            <link rel="preconnect" href="https://fonts.googleapis.com" />
            <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
            <link rel="stylesheet" :href="fontsHref" />
        </Head>
        <header class="ms2-topbar">
                    <div class="ms2-topbar-inner">
                        <a href="/" class="ms2-brand">
                            <span class="ms2-brand-mark" aria-hidden="true">
                                <svg viewBox="0 0 32 32" width="28" height="28" focusable="false">
                                    <circle cx="16" cy="16" r="6" fill="currentColor" />
                                    <g stroke="currentColor" stroke-width="2" stroke-linecap="round">
                                        <line x1="16" y1="2" x2="16" y2="6" />
                                        <line x1="16" y1="26" x2="16" y2="30" />
                                        <line x1="2" y1="16" x2="6" y2="16" />
                                        <line x1="26" y1="16" x2="30" y2="16" />
                                        <line x1="6" y1="6" x2="9" y2="9" />
                                        <line x1="23" y1="23" x2="26" y2="26" />
                                        <line x1="26" y1="6" x2="23" y2="9" />
                                        <line x1="9" y1="23" x2="6" y2="26" />
                                    </g>
                                </svg>
                            </span>
                            <span class="ms2-brand-text">
                                <strong>{{ brandName }}</strong>
                                <small>{{ brandTagline }}</small>
                            </span>
                        </a>
                        <nav class="ms2-topnav">
                            <a href="#ms2-dashboard">Dashboard-Vorschau</a>
                            <a href="#ms2-funktionen">Funktionen</a>
                            <a href="#ms2-account" class="ms2-topnav-cta">Account starten</a>
                            <a href="/ratgeber">Ratgeber</a>
                        </nav>
                    </div>
                </header>
        <slot />
        <nav class="ms2-legal" aria-label="Rechtliche Hinweise">
                    <a href="/impressum">Impressum</a>
                    <span aria-hidden="true">·</span>
                    <a href="/datenschutz">Datenschutz</a>
                    <span aria-hidden="true">·</span>
                    <a href="/agb">AGB</a>
                    <span aria-hidden="true">·</span>
                    <a href="/widerruf">Widerruf</a>
                    <a href="/ratgeber">Ratgeber</a>
                </nav>
        <footer class="ms2-foot">
                    <div class="ms2-foot-inner">
                        <p class="ms2-byline" v-if="author">
                            Redaktionell verantwortet von
                            <strong>{{ author.name }}</strong>{{ author.role ? ' - ' + author.role : '' }}.
                        </p>
                        <p class="ms2-byline" v-else>
                            Redaktionell verantwortet vom My Solar 24 Redaktionsteam.
                        </p>
                        <p class="ms2-foot-version">{{ DESIGN_VERSION }}</p>
                    </div>
                </footer>
    </div>
</template>

<style scoped>

.ms2-root {
    --ms2-accent: #3B82F6;
    --ms2-ink: #0B1220;
    --ms2-ink-soft: #475569;
    --ms2-bg: #F8FAFC;
    --ms2-surface: #FFFFFF;
    --ms2-line: #E2E8F0;
    --ms2-radius: 14px;
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    color: var(--ms2-ink);
    background: var(--ms2-bg);
    line-height: 1.55;
}
.ms2-root :where(h1, h2, h3) {
    font-family: 'Space Grotesk', 'Inter', system-ui, sans-serif;
    letter-spacing: -0.01em;
    color: var(--ms2-ink);
}

.ms2-topbar {
    position: sticky; top: 0; z-index: 30;
    background: rgba(255,255,255,0.92);
    backdrop-filter: saturate(150%) blur(8px);
    border-bottom: 1px solid var(--ms2-line);
}
.ms2-topbar-inner {
    max-width: 1180px; margin: 0 auto; padding: 14px 24px;
    display: flex; align-items: center; justify-content: space-between; gap: 24px;
}
.ms2-brand { display: flex; align-items: center; gap: 12px; text-decoration: none; color: inherit; }
.ms2-brand-mark { color: var(--ms2-accent); display: inline-flex; }
.ms2-brand-text strong { display: block; font-family: 'Space Grotesk', sans-serif; font-size: 17px; }
.ms2-brand-text small { color: var(--ms2-ink-soft); font-size: 12px; }
.ms2-topnav { display: flex; align-items: center; gap: 24px; }
.ms2-topnav a { color: var(--ms2-ink-soft); text-decoration: none; font-size: 14px; }
.ms2-topnav a:hover { color: var(--ms2-ink); }
.ms2-topnav-cta {
    background: var(--ms2-accent); color: #fff !important;
    padding: 8px 14px; border-radius: 999px;
    font-weight: 600; font-size: 13px;
}

.ms2-hero { padding: 64px 24px 48px; }
.ms2-hero-inner {
    max-width: 1180px; margin: 0 auto;
    display: grid; grid-template-columns: 1.05fr 1.15fr; gap: 56px; align-items: center;
}
.ms2-eyebrow {
    display: inline-block;
    background: rgba(59,130,246,0.10); color: var(--ms2-accent);
    padding: 6px 12px; border-radius: 999px;
    font-size: 12px; letter-spacing: 0.04em; text-transform: uppercase; font-weight: 600;
}
.ms2-hero-title { font-size: clamp(36px, 4.6vw, 56px); line-height: 1.05; margin: 20px 0 18px; }
.ms2-hl { color: var(--ms2-accent); }
.ms2-hero-sub { font-size: 18px; color: var(--ms2-ink-soft); max-width: 520px; margin-bottom: 22px; }
.ms2-hero-bullets { list-style: none; padding: 0; margin: 0 0 28px; display: grid; gap: 10px; }
.ms2-hero-bullets li { display: flex; align-items: center; gap: 12px; font-size: 15px; }
.ms2-bullet-dot {
    width: 10px; height: 10px; border-radius: 3px;
    background: var(--ms2-accent); display: inline-block;
}
.ms2-cta-primary {
    display: inline-block; background: var(--ms2-accent); color: #fff;
    padding: 16px 28px; border-radius: 12px; text-decoration: none;
    font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
    font-size: 14px; border: none; cursor: pointer;
    box-shadow: 0 10px 24px -10px rgba(59,130,246,0.55);
    transition: transform 0.15s ease;
}
.ms2-cta-primary:hover { transform: translateY(-1px); }
.ms2-cta-primary:disabled { opacity: 0.6; cursor: progress; }
.ms2-cta-full { display: block; width: 100%; }
.ms2-hero-trust { font-size: 12.5px; color: var(--ms2-ink-soft); margin-top: 14px; }

.ms2-hero-preview { position: relative; }
.ms2-window {
    background: var(--ms2-surface); border: 1px solid var(--ms2-line);
    border-radius: 18px; overflow: hidden;
    box-shadow: 0 30px 60px -30px rgba(15,23,42,0.30);
}
.ms2-window-bar {
    display: flex; align-items: center; gap: 8px;
    padding: 12px 16px; border-bottom: 1px solid var(--ms2-line);
    background: #F1F5F9;
}
.ms2-dot { width: 10px; height: 10px; border-radius: 50%; display: inline-block; }
.ms2-dot-red { background: #EF4444; }
.ms2-dot-amber { background: #F59E0B; }
.ms2-dot-green { background: #10B981; }
.ms2-window-url {
    margin-left: 14px; font-family: 'JetBrains Mono', ui-monospace, monospace;
    font-size: 12px; color: var(--ms2-ink-soft);
}
.ms2-window-body { padding: 22px; display: grid; gap: 18px; }
.ms2-dash-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }
.ms2-dash-kicker { font-size: 12px; color: var(--ms2-ink-soft); margin: 0; }
.ms2-dash-title { font-size: 22px; margin: 4px 0 0; }
.ms2-dash-pill {
    background: var(--ms2-accent); color: #fff;
    padding: 6px 12px; border-radius: 999px; font-weight: 600; font-size: 13px;
}
.ms2-stages {
    list-style: none; padding: 0; margin: 0;
    display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px;
}
.ms2-stages li {
    display: flex; flex-direction: column; align-items: center;
    gap: 6px; color: #94A3B8; font-size: 12px;
}
.ms2-stage-num {
    width: 28px; height: 28px; border-radius: 50%;
    border: 2px solid #CBD5E1; display: inline-flex; align-items: center; justify-content: center;
    font-weight: 700; font-size: 13px; color: #94A3B8; background: #fff;
}
.ms2-stages li.is-on { color: var(--ms2-ink); }
.ms2-stages li.is-on .ms2-stage-num { border-color: var(--ms2-accent); color: var(--ms2-accent); }
.ms2-progressbar {
    height: 6px; border-radius: 999px; background: #E2E8F0; overflow: hidden;
}
.ms2-progressbar > span {
    display: block; height: 100%;
    background: linear-gradient(90deg, var(--ms2-accent), #60A5FA);
    transition: width 0.4s ease;
}
.ms2-chart-card {
    border: 1px solid var(--ms2-line); border-radius: 12px; padding: 14px 14px 10px;
}
.ms2-chart-head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 4px; }
.ms2-chart-title { font-size: 12px; color: var(--ms2-ink-soft); text-transform: uppercase; letter-spacing: 0.05em; }
.ms2-chart-val { font-family: 'Space Grotesk', sans-serif; font-size: 16px; font-weight: 600; }
.ms2-chart-svg { width: 100%; height: 90px; display: block; }
.ms2-chart-axis { display: flex; justify-content: space-between; font-size: 10px; color: #94A3B8; padding: 4px 2px 0; }
.ms2-kpi-row { display: grid; grid-template-columns: 1fr 1.4fr; gap: 12px; }
.ms2-kpi { background: #F8FAFC; border-radius: 10px; padding: 12px 14px; }
.ms2-kpi-lbl { font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; color: var(--ms2-ink-soft); margin: 0; }
.ms2-kpi-val { font-family: 'Space Grotesk', sans-serif; font-weight: 600; font-size: 18px; margin: 4px 0 0; }
.ms2-kpi-val-sm { font-size: 14px; }

.ms2-features { padding: 64px 24px; background: var(--ms2-surface); border-block: 1px solid var(--ms2-line); }
.ms2-features-inner { max-width: 1180px; margin: 0 auto; }
.ms2-section-title { font-size: clamp(28px, 3vw, 36px); margin: 0 0 32px; }
.ms2-feature-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px; }
.ms2-feature {
    border: 1px solid var(--ms2-line); border-radius: var(--ms2-radius);
    padding: 26px; background: #fff;
}
.ms2-feature-num {
    font-family: 'Space Grotesk', sans-serif; font-weight: 700;
    color: var(--ms2-accent); font-size: 13px; letter-spacing: 0.08em;
    margin-bottom: 12px;
}
.ms2-feature h3 { font-size: 18px; margin: 0 0 8px; }
.ms2-feature p { font-size: 14.5px; color: var(--ms2-ink-soft); margin: 0; }

.ms2-account { padding: 72px 24px; }
.ms2-account-inner {
    max-width: 1180px; margin: 0 auto;
    display: grid; grid-template-columns: 1fr 1.05fr; gap: 56px; align-items: start;
}
.ms2-account-lead { font-size: 17px; color: var(--ms2-ink-soft); }
.ms2-account-list { list-style: none; padding: 0; margin: 18px 0 0; display: grid; gap: 8px; }
.ms2-account-list li { padding-left: 18px; position: relative; font-size: 14.5px; }
.ms2-account-list li::before {
    content: ''; position: absolute; left: 0; top: 9px;
    width: 8px; height: 8px; border-radius: 2px; background: var(--ms2-accent);
}
.ms2-account-form {
    background: #fff; border: 1px solid var(--ms2-line); border-radius: 18px;
    padding: 28px; display: grid; gap: 16px;
    box-shadow: 0 20px 40px -28px rgba(15,23,42,0.25);
}
.ms2-field { display: grid; gap: 6px; }
.ms2-field-lbl { font-size: 13px; font-weight: 600; color: var(--ms2-ink); }
.ms2-field input, .ms2-select-wrap select {
    width: 100%; padding: 12px 14px; border-radius: 10px;
    border: 1px solid var(--ms2-line); font-size: 15px; background: #fff;
    font-family: inherit; color: var(--ms2-ink);
}
.ms2-field input:focus, .ms2-select-wrap select:focus {
    outline: 2px solid var(--ms2-accent); outline-offset: 1px; border-color: var(--ms2-accent);
}
.ms2-select-wrap { position: relative; }
.ms2-select-wrap::after {
    content: ''; position: absolute; right: 16px; top: 50%;
    width: 8px; height: 8px; border-right: 2px solid var(--ms2-ink-soft);
    border-bottom: 2px solid var(--ms2-ink-soft); transform: translateY(-70%) rotate(45deg);
    pointer-events: none;
}
.ms2-select-wrap select { appearance: none; padding-right: 36px; }
.ms2-consent {
    display: grid; grid-template-columns: 22px 1fr; gap: 10px; align-items: flex-start;
    font-size: 12.5px; color: var(--ms2-ink-soft); line-height: 1.5;
}
.ms2-consent input { margin-top: 3px; }
.ms2-error {
    background: #FEF2F2; color: #991B1B; border: 1px solid #FECACA;
    padding: 10px 12px; border-radius: 8px; margin: 0; font-size: 13.5px;
}
.ms2-account-success {
    background: #fff; border: 1px solid var(--ms2-line); border-radius: 18px;
    padding: 32px; box-shadow: 0 20px 40px -28px rgba(15,23,42,0.25);
}
.ms2-account-success h3 { font-size: 22px; margin: 0 0 10px; color: var(--ms2-accent); }
.ms2-success-next { margin-top: 14px; font-size: 14px; color: var(--ms2-ink-soft); }

.ms2-foot { padding: 32px 24px 48px; border-top: 1px solid var(--ms2-line); background: #fff; }
.ms2-foot-inner {
    max-width: 1180px; margin: 0 auto;
    display: flex; justify-content: space-between; gap: 16px; flex-wrap: wrap;
}
.ms2-byline { font-size: 13px; color: var(--ms2-ink-soft); margin: 0; }
.ms2-foot-version { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; color: #94A3B8; margin: 0; }

@media (max-width: 900px) {
    .ms2-hero-inner, .ms2-feature-grid, .ms2-account-inner {
        grid-template-columns: 1fr;
    }
    .ms2-topnav a:not(.ms2-topnav-cta) { display: none; }
}

/* ── Trust strip (images + cites + byline) — ms2-* unique treatment ── */
.ms2-trustbar {
    background: #f7f3ea;
    padding: 32px 18px;
    border-top: 1px solid rgba(20,17,13,0.08);
    border-bottom: 1px solid rgba(20,17,13,0.08);
}
.ms2-trustbar__inner {
    max-width: 1120px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr;
    gap: 22px;
    align-items: start;
}
@media (min-width: 820px) {
    .ms2-trustbar__inner {
        grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
        gap: 36px;
    }
}
.ms2-trustbar__photo {
    margin: 0;
    width: 100%;
    aspect-ratio: 4 / 3;
    border-radius: 18px;
    overflow: hidden;
    position: relative;
    background: #fff;
    border: 1px solid #3B82F6aa; box-shadow: 0 0 0 4px #3B82F61a, 0 10px 30px -16px #3B82F644;
}
.ms2-trustbar__photo img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}
.ms2-trustbar__chip {
    position: absolute;
    top: 12px;
    left: 12px;
    background: rgba(255,255,255,0.94);
    color: #14110d;
    font-size: 11px;
    letter-spacing: 0.16em;
    text-transform: uppercase;
    font-weight: 600;
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid rgba(20,17,13,0.08);
    backdrop-filter: blur(6px);
    margin: 0;
}
.ms2-trustbar__body {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.ms2-trustbar__lead {
    margin: 0;
    color: #3f3a32;
    font-size: 15px;
    line-height: 1.5;
    font-family: 'Inter', system-ui, sans-serif;
}
.ms2-trustbar__pills {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.ms2-trustbar__pill {
    display: inline-flex;
    align-items: center;
    border: 1px solid #3B82F655;
    color: #14110d;
    background: #fff;
    border-radius: 999px;
    padding: 6px 12px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13px;
    font-weight: 500;
    letter-spacing: 0.04em;
}
.ms2-trustbar__byline {
    display: flex;
    align-items: center;
    gap: 12px;
}
.ms2-trustbar__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #3B82F6;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: 'Inter', system-ui, sans-serif;
    font-weight: 700;
    font-size: 13px;
    letter-spacing: 0.02em;
    flex-shrink: 0;
}
.ms2-trustbar__bytxt {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
}
.ms2-trustbar__byname {
    font-size: 13px;
    font-weight: 600;
    color: #14110d;
}
.ms2-trustbar__byrole {
    font-size: 12px;
    color: #3f3a32;
}
.ms2-trustbar__feature {
    margin: 8px 0 0;
    max-width: 360px;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid rgba(20,17,13,0.10);
    box-shadow: 0 6px 22px -16px rgba(20,17,13,0.20);
    background: #fff;
}
.ms2-trustbar__feature img {
    width: 100%;
    height: auto;
    aspect-ratio: 16 / 10;
    object-fit: cover;
    display: block;
}
.ms2-trustbar__feature figcaption {
    font-size: 11px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #3f3a32;
    padding: 8px 12px;
    background: #fff;
}


.ms2-legal{margin:32px auto 16px;text-align:center;font-size:13px;color:#9ca3af}.ms2-legal a{color:inherit;text-decoration:none;margin:0 8px}.ms2-legal a:hover{color:#3B82F6;text-decoration:underline}

</style>
